"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrganisationDomain = void 0;
const firestore_1 = require("firebase/firestore");
const storage_1 = require("firebase/storage");
const storage_2 = require("rxfire/storage");
const short_uuid_1 = __importDefault(require("short-uuid"));
const flow_domain_1 = require("./flow-domain");
const OrganisationDomainError_1 = __importDefault(require("../errors/OrganisationDomainError"));
const helpers_1 = require("../helpers");
const firebase_1 = require("../services/firebase");
const use_upflowy_store_1 = require("../use-upflowy-store");
const CONFIG_ID_REGEX = /^public\/remote-config\/([-0-9a-zA-Z]+)\.json$/;
// eslint-disable-next-line import/prefer-default-export
class OrganisationDomain {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    constructor(db, ref) {
        this.db = db;
        this.ref = ref;
        this.doc = null;
        return this;
    }
    static findById(db, organisationId) {
        const organisationRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(db, 'Organisations'), organisationId);
        return new OrganisationDomain(db, organisationRef);
    }
    async getDoc() {
        if (!this.doc) {
            this.doc = (await (0, firestore_1.getDoc)(this.ref)).data();
        }
        return this.doc;
    }
    async getRemoteConfigId() {
        const { remoteConfigFileName } = await this.getDoc();
        const [, id] = remoteConfigFileName.match(CONFIG_ID_REGEX);
        return id;
    }
    async getRemoteConfigInfo() {
        const { remoteConfigFileName } = await this.getDoc();
        if (!remoteConfigFileName) {
            throw new OrganisationDomainError_1.default(`Remote Config Filename not found on organisation id ${this.ref.id}`);
        }
        const downloadURL = await (0, storage_2.getDownloadURL)((0, storage_1.ref)(firebase_1.storage, remoteConfigFileName)).toPromise();
        if (!downloadURL) {
            throw new OrganisationDomainError_1.default(`Download URL not found on organisation id ${this.ref.id}`);
        }
        const config = await fetch(downloadURL).then((response) => response.json());
        const id = await this.getRemoteConfigId();
        const { subdomain } = await this.getDoc();
        return {
            id,
            url: (0, helpers_1.getStorageUrl)(remoteConfigFileName),
            config: {
                ...config,
                ...(subdomain && { subdomain }),
            },
        };
    }
    async setRemoteConfig(config) {
        const { remoteConfigFileName } = await this.getDoc();
        if (!remoteConfigFileName) {
            throw new OrganisationDomainError_1.default(`Remote Config Filename not found on organisation id ${this.ref.id}`);
        }
        const id = await this.getRemoteConfigId();
        const remoteConfigAsString = JSON.stringify(config);
        const blob = new Blob([remoteConfigAsString], { type: 'application/json' });
        await (0, storage_1.uploadBytes)((0, storage_1.ref)(firebase_1.storage, remoteConfigFileName), blob, {
            contentType: 'application/json',
            cacheControl: 'public,must-revalidate,max-age=30,s-maxage=60',
            customMetadata: {
                lastModified: `${new Date().getTime()}`,
                organisationId: this.ref.id,
            },
        });
        return {
            id,
            url: (0, helpers_1.getStorageUrl)(remoteConfigFileName),
            config,
        };
    }
    async setFlowTriggersInRemoteConfig({ flowId, remoteConfigTriggers, }) {
        const { config } = await this.getRemoteConfigInfo();
        const updatedTriggers = remoteConfigTriggers;
        config.triggers.forEach((trigger) => {
            // only keeping trigger that belongs to other flows
            if (trigger.flowId !== flowId) {
                updatedTriggers.push(trigger);
            }
        });
        await this.setRemoteConfig({
            ...config,
            triggers: updatedTriggers,
        });
        return this;
    }
    async removeFlowTriggersFromRemoteConfig(flowId) {
        const { config } = await this.getRemoteConfigInfo();
        const updatedTriggers = config.triggers.filter((trigger) => trigger.flowId !== flowId);
        await this.setRemoteConfig({
            ...config,
            triggers: updatedTriggers,
        });
        return this;
    }
    async getFlows() {
        const qry = (0, firestore_1.query)((0, firestore_1.collection)(this.db, 'Flows'), (0, firestore_1.where)('organisationId', '==', this.ref.id));
        const flows = (await (0, firestore_1.getDocs)(qry)).docs;
        if (!flows.length)
            return null;
        return flows;
    }
    async forceRefreshRemoteConfig() {
        const { config } = await this.getRemoteConfigInfo();
        const flows = await this.getFlows();
        if (!flows) {
            return this.setRemoteConfig({
                ...config,
                triggers: [],
            });
        }
        const remoteConfigTriggers = [];
        await Promise.all(flows.map(async (flow) => {
            const flowDomain = flow_domain_1.FlowDomain.findById(this.db, flow.ref.id);
            const liveVersionRef = await flowDomain.getLiveVersionRef();
            if (liveVersionRef) {
                const liveVersionTriggers = await flowDomain.getRemoteConfigTriggers(liveVersionRef);
                liveVersionTriggers.forEach((trigger) => remoteConfigTriggers.push(trigger));
            }
        }));
        return this.setRemoteConfig({
            ...config,
            triggers: remoteConfigTriggers,
        });
    }
}
exports.OrganisationDomain = OrganisationDomain;
OrganisationDomain.constructRemoteConfigFileName = () => `public/remote-config/${(0, short_uuid_1.default)().uuid()}.json`;
//# sourceMappingURL=organisation-domain.js.map